import { Head, router, useForm } from "@inertiajs/react"
import { Trash01 } from "@untitledui/icons"
import { format, formatDistanceToNow } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InputBase, Label, TextField } from "@/components/ui/input"
import { Panel } from "@/components/ui/panel"
import { NativeSelect } from "@/components/ui/select"
import { Table, TableCard } from "@/components/ui/table"
import { AdminLayout } from "@/layouts/admin-layout"
import { toFieldError } from "@/lib/utils"
import {
  adminAllowlistedNumberPath,
  adminAllowlistedNumbersPath,
} from "@/routes"

import type { AllowlistedNumberRow, TermOption } from "./types"

interface Props {
  numbers: AllowlistedNumberRow[]
  floor: string[]
  guarded: boolean
  terms: TermOption[]
}

/** The keys the controller can send back; the ones with a field of their own render beside it. */
const FIELD_ERRORS = ["number", "label", "term_days"]

interface FormData {
  number: string
  label: string
  term_days: string
  [key: string]: string
}

const day = (iso: string) => format(new Date(iso), "d MMM")

const expiry = (row: AllowlistedNumberRow) => {
  if (!row.expires_at) return "Until removed"
  if (row.expired) return `Expired ${day(row.expires_at)}`
  return `${formatDistanceToNow(new Date(row.expires_at), { addSuffix: true })}, ${day(row.expires_at)}`
}

/**
 * Who this environment may text or ring for real. The floor comes from the
 * service's environment and needs a restart to change; the rows below are in
 * force at the next send or dial and go when they expire. Production runs
 * live transports and never consults either.
 */
export default function AdminAllowlistIndex({
  numbers,
  floor,
  guarded,
  terms,
}: Props) {
  const form = useForm<FormData>({
    number: "",
    label: "",
    term_days: String(
      terms.find((t) => t.value === 7)?.value ?? terms[0]?.value ?? 7,
    ),
  })
  const otherErrors = Object.entries(form.errors).filter(
    ([key]) => !FIELD_ERRORS.includes(key),
  )

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ allowlisted_number: data }))
    form.post(adminAllowlistedNumbersPath(), {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  return (
    <AdminLayout>
      <Head title="Allowlist" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "call list" }, { label: "allowlist" }]}
          title="Allowlist"
          description={
            guarded
              ? "Who this environment may text or ring for real. Everyone else is logged and never reaches the carrier. A number added here is reachable at the next send or dial."
              : "This environment's transports are live and reach everyone; the list is not consulted here."
          }
        />

        <Panel
          title="Always reachable"
          description="From SMS_ALLOWLIST on the service. Read once at boot, so a change there needs a restart; the team's own mobiles belong here."
        >
          {floor.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {floor.map((entry) => (
                <Badge key={entry} type="modern" color="gray" size="sm">
                  {entry}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-tertiary text-sm">Empty: nobody, by default.</p>
          )}
        </Panel>

        <Panel
          title="Add a number"
          description="For a showcase or a demo. It expires on its own; remove it sooner from the list below."
        >
          <form
            onSubmit={submit}
            className="flex flex-col gap-3 md:flex-row md:items-end"
          >
            <TextField
              isRequired
              value={form.data.number}
              onChange={(value: string) => form.setData("number", value)}
              isInvalid={!!form.errors.number}
              error={toFieldError(form.errors.number)}
              className="md:w-56"
            >
              <Label>Number</Label>
              <InputBase size="sm" placeholder="0400 000 000" />
            </TextField>
            <TextField
              isRequired
              value={form.data.label}
              onChange={(value: string) => form.setData("label", value)}
              isInvalid={!!form.errors.label}
              error={toFieldError(form.errors.label)}
              className="md:flex-1"
            >
              <Label>Who</Label>
              <InputBase size="sm" placeholder="Jo, Ipswich showcase" />
            </TextField>
            <NativeSelect
              label="For"
              value={form.data.term_days}
              onChange={(e) => form.setData("term_days", e.target.value)}
              options={terms.map((t) => ({
                label: t.label,
                value: String(t.value),
              }))}
              className="md:w-44"
            />
            <Button
              size="md"
              color="primary"
              type="submit"
              isLoading={form.processing}
            >
              Add
            </Button>
          </form>
          {otherErrors.length > 0 && (
            <ul className="text-error-primary flex flex-col gap-1 text-sm">
              {otherErrors.map(([key, message]) => (
                <li key={key}>
                  {key === "base" ? "" : `${key.replace(/_/g, " ")} `}
                  {message}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <TableCard.Root bleed>
          <TableCard.Caption
            title="Reachable numbers"
            count={`${numbers.length} added`}
          />
          {numbers.length === 0 ? (
            <TableCard.Empty>
              No numbers added yet — only the floor above can be reached from
              here.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Allowlisted numbers">
                <Table.Header>
                  <Table.Head id="number" isRowHeader label="Number" />
                  <Table.Head id="label" label="Who" className="w-full" />
                  <Table.Head id="added" label="Added" />
                  <Table.Head id="expires" label="Reachable" />
                  <Table.Head
                    id="actions"
                    label=""
                    className="w-ledger-actions"
                  />
                </Table.Header>
                <Table.Body items={numbers}>
                  {(row) => (
                    <Table.Row id={row.id}>
                      <Table.Id>{row.number}</Table.Id>
                      <Table.Lead>{row.label}</Table.Lead>
                      <Table.Cell>
                        {row.added_by}, {day(row.added_on)}
                      </Table.Cell>
                      {row.expired ? (
                        <Table.Status tier="system">{expiry(row)}</Table.Status>
                      ) : (
                        <Table.Cell>{expiry(row)}</Table.Cell>
                      )}
                      <Table.Actions>
                        <Button
                          size="sm"
                          color="tertiary"
                          iconLeading={Trash01}
                          aria-label="Remove"
                          onPress={() =>
                            router.delete(adminAllowlistedNumberPath(row.id), {
                              preserveScroll: true,
                            })
                          }
                        />
                      </Table.Actions>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer total={numbers.length} />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
